"use client";

import type { MultiSelectOption, SelectionIndicatorStyle } from "./form-types";

import { cn } from "@homeinstead/ui";
import Image from "next/image";
import { DefaultCheckboxCardIndicator } from "./DefaultCheckboxCardIndicator";
import { MultiSelectIndicatorSlot } from "./MultiSelectIndicatorSlot";

export interface MultiSelectCheckboxCardOptionProps {
  option: MultiSelectOption;
  checked: boolean;
  disabled: boolean;
  fieldName: string;
  onChange: (value: string, checked: boolean) => void;
  selectionIndicator?: SelectionIndicatorStyle | null;
}

export function MultiSelectCheckboxCardOption({
  option,
  checked,
  disabled,
  fieldName,
  onChange,
  selectionIndicator,
}: MultiSelectCheckboxCardOptionProps) {
  const optionValue = option.value ?? option.label ?? option._key;
  const inputName = `${fieldName}.${optionValue}`;
  const isDisabled = disabled && !checked;

  return (
    <label
      className={cn(
        "flex w-full cursor-pointer justify-between gap-3 rounded-sm border-2 border-transparent bg-white p-4 text-new-beginnings-base focus-within:outline focus-within:outline-2 focus-within:outline-new-beginnings-base focus-within:outline-offset-2 hover:border-new-beginnings-base hover:bg-new-beginnings-tints-800 hover:shadow-primary-button",
        option.description ? "h-[104px] items-start" : "h-[72px] items-center",
        {
          "border-new-beginnings-base bg-new-beginnings-tints-800": checked,
          "cursor-not-allowed opacity-50": isDisabled,
        },
      )}
      htmlFor={inputName}
    >
      <input
        checked={checked}
        className="sr-only"
        disabled={isDisabled}
        id={inputName}
        name={inputName}
        onChange={(e) => onChange(optionValue, e.target.checked)}
        type="checkbox"
      />
      <div className="flex min-w-0 flex-1 gap-3">
        {option.iconUrl && (
          <Image
            alt=""
            aria-hidden="true"
            className="mt-0.5 size-5 shrink-0"
            height={20}
            src={option.iconUrl}
            width={20}
          />
        )}
        <div className="flex flex-col items-start gap-1">
          <span className="font-bold text-md">{option.label}</span>
          {option.description && (
            <span className="font-normal text-default">
              {option.description}
            </span>
          )}
        </div>
      </div>
      {selectionIndicator === "checkmark" ? (
        <MultiSelectIndicatorSlot isSelected={checked} style="checkmark" />
      ) : (
        <DefaultCheckboxCardIndicator checked={checked} />
      )}
    </label>
  );
}
